import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Grid,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Repeat as RepeatIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useTranslation } from '../../utils/i18n-utils';
import { 
  getEmployee, 
  getEmployeeShifts, 
  createEmployeeShift,
  deleteEmployeeShift, 
  Employee, 
  EmployeeShift 
} from '../../api/employees';
import { format } from 'date-fns';

const EmployeeShifts: React.FC = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [schedules, setSchedules] = useState<EmployeeShift[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Schedule settings
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedDays, setSelectedDays] = useState<{
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  }>({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false
  });
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [isRecurring, setIsRecurring] = useState<boolean>(true);
  const [recurringPattern, setRecurringPattern] = useState<'weekly' | 'monthly'>('weekly');
  const [notes, setNotes] = useState<string>('');

  // Notification state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    if (employeeId) {
      fetchEmployeeData();
      fetchSchedules();
    }
  }, [employeeId]);

  const fetchEmployeeData = async () => {
    try {
      const data = await getEmployee(Number(employeeId));
      setEmployee(data);
    } catch (err: any) {
      console.error('Failed to fetch employee:', err);
      setError('Failed to load employee data.');
    }
  };

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      // Getting all shifts regardless of date to show the schedule pattern
      const data = await getEmployeeShifts(Number(employeeId));
      setSchedules(data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch schedules:', err);
      setError('Failed to load schedule data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    // Reset form
    setSelectedDays({
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    });
    setStartTime(null);
    setEndTime(null);
    setIsRecurring(true);
    setRecurringPattern('weekly');
    setNotes('');
    
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDayToggle = (day: keyof typeof selectedDays) => {
    setSelectedDays({
      ...selectedDays,
      [day]: !selectedDays[day],
    });
  };

  const handleSubmitSchedule = async () => {
    // Validate inputs
    if (Object.values(selectedDays).every(day => day === false)) {
      setSnackbar({
        open: true,
        message: 'Please select at least one day of the week.',
        severity: 'error'
      });
      return;
    }

    if (!startTime || !endTime) {
      setSnackbar({
        open: true,
        message: 'Please set both start and end time.',
        severity: 'error'
      });
      return;
    }

    try {
      // Get array of day names that are selected
      const selectedDayNames = Object.entries(selectedDays)
        .filter(([_, isSelected]) => isSelected)
        .map(([day]) => day);

      // Create a shift for each selected day starting from next occurrence
      const today = new Date();
      const dayMap: Record<string, number> = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6
      };

      // Format times
      const startTimeStr = format(startTime, 'HH:mm:ss');
      const endTimeStr = format(endTime, 'HH:mm:ss');

      // Create shifts for the next occurrence of each selected day
      const shiftPromises = selectedDayNames.map(async (dayName) => {
        // Calculate the next occurrence of this weekday
        const targetDay = dayMap[dayName];
        const currentDay = today.getDay();
        
        // Days until the next occurrence
        const daysUntilNext = (targetDay - currentDay + 7) % 7;
        
        // Date of the next occurrence
        const nextOccurrence = new Date(today);
        nextOccurrence.setDate(today.getDate() + daysUntilNext);
        
        // Format date for API
        const dateStr = format(nextOccurrence, 'yyyy-MM-dd');
        
        // Create the shift
        const shiftData = {
          date: dateStr,
          startTime: startTimeStr,
          endTime: endTimeStr,
          status: 'scheduled' as const,
          isRecurring,
          recurringPattern: isRecurring ? recurringPattern : undefined,
          notes: `${notes} (Regular ${dayName} shift)`
        };
        
        return createEmployeeShift(Number(employeeId), shiftData);
      });
      
      await Promise.all(shiftPromises);
      
      setSnackbar({
        open: true,
        message: 'Work schedule created successfully!',
        severity: 'success'
      });
      
      handleCloseDialog();
      fetchSchedules();
    } catch (err: any) {
      console.error('Error saving schedule:', err);
      setSnackbar({
        open: true,
        message: 'Failed to save schedule. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleClearSchedule = async () => {
    if (window.confirm('Are you sure you want to clear all scheduled shifts? This cannot be undone.')) {
      try {
        // Delete all shifts for this employee
        const deletePromises = schedules.map(schedule => 
          deleteEmployeeShift(Number(employeeId), schedule.id)
        );
        
        await Promise.all(deletePromises);
        
        setSnackbar({
          open: true,
          message: 'All scheduled shifts have been cleared.',
          severity: 'success'
        });
        
        fetchSchedules();
      } catch (err: any) {
        console.error('Error clearing schedule:', err);
        setSnackbar({
          open: true,
          message: 'Failed to clear schedule. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Group shifts by day of week for display
  const getScheduleByDay = () => {
    const daySchedules: Record<string, {day: string, shifts: EmployeeShift[]}> = {
      monday: {day: 'Monday', shifts: []},
      tuesday: {day: 'Tuesday', shifts: []},
      wednesday: {day: 'Wednesday', shifts: []},
      thursday: {day: 'Thursday', shifts: []},
      friday: {day: 'Friday', shifts: []},
      saturday: {day: 'Saturday', shifts: []},
      sunday: {day: 'Sunday', shifts: []}
    };
    
    schedules.forEach(shift => {
      const shiftDate = new Date(shift.date);
      const dayOfWeek = shiftDate.getDay();
      
      // Map day number to name
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayName = dayNames[dayOfWeek];
      
      if (dayName && daySchedules[dayName]) {
        daySchedules[dayName].shifts.push(shift);
      }
    });
    
    return Object.values(daySchedules);
  };
  
  // Delete a single shift
  const handleDeleteShift = async (shiftId: number) => {
    if (window.confirm('Are you sure you want to delete this shift?')) {
      try {
        await deleteEmployeeShift(Number(employeeId), shiftId);
        setSnackbar({
          open: true,
          message: 'Shift deleted successfully!',
          severity: 'success'
        });
        fetchSchedules();
      } catch (err: any) {
        console.error('Error deleting shift:', err);
        setSnackbar({
          open: true,
          message: 'Failed to delete shift. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs navigation */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link 
          color="inherit" 
          href="#" 
          onClick={(e) => { e.preventDefault(); navigate('/dashboard/accounting/employees'); }}
        >
          Employees
        </Link>
        <Typography color="text.primary">
          {employee?.name || 'Employee'} Work Schedule
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button 
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard/accounting/employees')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4">
            {employee?.name} - Work Schedule
          </Typography>
        </Box>
        <Box>
          <Button 
            variant="outlined" 
            color="error" 
            onClick={handleClearSchedule}
            sx={{ mr: 2 }}
          >
            Clear Schedule
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Set Schedule
          </Button>
        </Box>
      </Box>

      {/* Employee Info Card */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">Name</Typography>
            <Typography variant="body1">{employee?.name}</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">Mobile</Typography>
            <Typography variant="body1">{employee?.mobileNumber}</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">Pay Rate</Typography>
            <Typography variant="body1">${employee?.rateAmount} ({employee?.rateType})</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">Status</Typography>
            <Chip 
              label={employee?.isActive ? "Active" : "Inactive"} 
              color={employee?.isActive ? "success" : "default"} 
              size="small" 
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Weekly Schedule Display */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {getScheduleByDay().map((dayData) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={dayData.day}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  {dayData.day}
                </Typography>
                
                {dayData.shifts.length === 0 ? (
                  <Typography color="text.secondary">No shifts scheduled</Typography>
                ) : (
                  <List dense>
                    {dayData.shifts.map((shift) => (
                      <ListItem key={shift.id}>
                        <ListItemText
                          primary={`${shift.startTime || '??'} - ${shift.endTime || '??'}`}
                          secondary={
                            <>
                              {shift.isRecurring && (
                                <Chip 
                                  size="small" 
                                  icon={<RepeatIcon />} 
                                  label={shift.recurringPattern || 'Recurring'} 
                                  sx={{ mr: 1, mb: 1 }}
                                />
                              )}
                              {shift.notes}
                            </>
                          }
                        />
                        <IconButton 
                          size="small" 
                          edge="end" 
                          color="error"
                          onClick={() => handleDeleteShift(shift.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Schedule Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Set Work Schedule
        </DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Working Days
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {Object.entries(selectedDays).map(([day, isSelected]) => (
                <Chip
                  key={day}
                  label={day.charAt(0).toUpperCase() + day.slice(1)}
                  onClick={() => handleDayToggle(day as keyof typeof selectedDays)}
                  color={isSelected ? "primary" : "default"}
                  variant={isSelected ? "filled" : "outlined"}
                  sx={{ textTransform: 'capitalize' }}
                />
              ))}
            </Box>
            
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TimePicker
                  label="Start Time"
                  value={startTime}
                  onChange={(time) => setStartTime(time)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TimePicker
                  label="End Time"
                  value={endTime}
                  onChange={(time) => setEndTime(time)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>
            
            <FormControl fullWidth margin="dense">
              <InputLabel>Recurring</InputLabel>
              <Select
                value={isRecurring ? "true" : "false"}
                onChange={(e) => setIsRecurring(e.target.value === "true")}
              >
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
            
            {isRecurring && (
              <FormControl fullWidth margin="dense">
                <InputLabel>Recurring Pattern</InputLabel>
                <Select
                  value={recurringPattern}
                  onChange={(e) => setRecurringPattern(e.target.value as 'weekly' | 'monthly')}
                >
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            )}
            
            <TextField
              margin="dense"
              label="Notes"
              fullWidth
              multiline
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmitSchedule} variant="contained" color="primary">
            Save Schedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmployeeShifts; 